/**
 * save-state.js — the two-axis save model, as a pure reducer.
 *
 * Contracts §6. `autosave:error` means **local persistence failed**. It is not
 * "offline", and collapsing the two would delete the product's only
 * error-and-retry affordance — the one state that can actually lose work.
 *
 * ── Two axes, because they are two facts ──────────────────────────────────
 *   local  : dirty | saving | saved | error       — did the bytes reach disk
 *   cloud  : none | pending | syncing | synced | failed | conflict | paused |
 *            offline | unavailable                — did they reach the account
 *
 * BC-2 widened the cloud axis. It used to know only online / offline /
 * auth-paused, so "Saved" could not say WHERE: a project saved in this browser
 * and waiting 30 seconds for its next sync tick looked identical to one already
 * in the account, and a failing sync (a rate-limited `console.warn`) and a
 * conflict (a toast) never reached the indicator at all. Builder-coherence
 * contracts §2 is the table below, in prose.
 *
 * ── Precedence ────────────────────────────────────────────────────────────
 *   local error > conflict > cloud failed > unavailable > paused >
 *   saving | syncing > dirty > pending > saved (split by where)
 *
 * Local failure outranks everything because it is the only state that can lose
 * work. A conflict outranks a failure because retrying a conflict re-hits it.
 *
 * ── Retry retries the thing that failed ───────────────────────────────────
 * The error view's click was `toolbar:quick-save`, which DOWNLOADS a .pb.json —
 * and the pill then read the download's `save:completed` as a successful save
 * and flipped "Save failed" to "Saved now" while the disk write was still
 * failing. Retry is now `toolbar:retry-save` (local) or
 * `toolbar:retry-cloud-sync` (cloud); a download is a backup and moves neither
 * axis.
 *
 * Pure and exported so the table can be tested exhaustively — every local ×
 * cloud combination, not the ones anyone thought to check by hand.
 */

/** @typedef {'dirty'|'saving'|'saved'|'error'} LocalState */
/** @typedef {'none'|'pending'|'syncing'|'synced'|'failed'|'conflict'|'paused'|'offline'|'unavailable'} CloudState */

export const LOCAL_STATES = Object.freeze(['dirty', 'saving', 'saved', 'error']);
export const CLOUD_STATES = Object.freeze([
  'none',
  'pending',
  'syncing',
  'synced',
  'failed',
  'conflict',
  'paused',
  'offline',
  'unavailable'
]);

/** Commands a view can name. The pill emits them; the shell answers them. */
export const SAVE_COMMANDS = Object.freeze({
  retryLocal: 'toolbar:retry-save',
  retryCloud: 'toolbar:retry-cloud-sync',
  resolveConflict: 'toolbar:resolve-cloud-conflict',
  signIn: 'toolbar:auth-show',
  openPopover: 'command-bar:open-save-popover'
});

/**
 * What the pill shows, and what clicking it does.
 *
 * `click` is a COMMAND NAME, not a handler: the reducer stays free of the event
 * bus, and the pill's click path becomes one `emit(view.click)` rather than a
 * second copy of this precedence ladder written as if/else.
 *
 * `settled` is true only for the states in which nothing is wrong and nothing
 * is waiting — the narrow command bar collapses the label to a dot for those
 * and ONLY those (BC-10: a failure must never shrink to a colour).
 *
 * @param {{local: LocalState, cloud: CloudState, savedAt?: number|null, now?: number, detail?: string}} input
 * @returns {{tone: string, label: string, title: string, click: string, pulsing: boolean, settled: boolean}}
 */
export function deriveSaveView({
  local = 'saved',
  cloud = 'none',
  savedAt = null,
  now,
  detail = ''
} = {}) {
  // `short` is the phone label (BC-10): row 1 on a 390px screen has room for a
  // word or two beside identity and Publish, and a failure must still be READ
  // there, not reduced to a coloured dot.
  const view = (tone, label, title, click = SAVE_COMMANDS.openPopover, extra = {}) => ({
    tone,
    label,
    short: label,
    title,
    click,
    pulsing: false,
    settled: false,
    ...extra
  });

  // ── local error: the only state that can lose work ──
  if (local === 'error') {
    return view(
      'error',
      'Save failed',
      'Your latest changes are not saved on this device. Click to retry.',
      SAVE_COMMANDS.retryLocal,
      { short: 'Not saved' }
    );
  }

  // ── cloud problems, most actionable first ──
  if (cloud === 'conflict') {
    return view(
      'paused',
      'Sync conflict',
      'This project changed on another device. Click to choose which version to keep.',
      SAVE_COMMANDS.resolveConflict,
      { short: 'Conflict' }
    );
  }
  if (cloud === 'failed') {
    return view(
      'paused',
      'Cloud sync failed',
      `Saved on this device, but not to your account${detail ? ` (${detail})` : ''}. Click to retry.`,
      SAVE_COMMANDS.retryCloud,
      { short: 'Sync failed' }
    );
  }
  if (cloud === 'unavailable') {
    return view(
      'paused',
      'Project unavailable',
      'This project is no longer available in your account. Your work is kept on this device.',
      SAVE_COMMANDS.openPopover,
      { short: 'Unavailable' }
    );
  }
  if (cloud === 'paused') {
    return view(
      'paused',
      'Session expired',
      'Cloud sync paused — your session expired. Click to sign in.',
      SAVE_COMMANDS.signIn,
      { short: 'Sign in' }
    );
  }

  // ── work in flight ──
  if (local === 'saving' || cloud === 'syncing') {
    return view(
      'saving',
      local === 'saving' ? 'Saving…' : 'Syncing…',
      local === 'saving' ? 'Saving your changes on this device.' : 'Saving to your account.',
      SAVE_COMMANDS.openPopover,
      { pulsing: true } // the ONLY state that animates; never at rest
    );
  }

  if (local === 'dirty') {
    return view(
      'dirty',
      'Unsaved changes',
      cloud === 'offline'
        ? 'Unsaved changes. You are offline — they will sync when you reconnect.'
        : 'You have unsaved changes.',
      SAVE_COMMANDS.openPopover,
      { short: 'Unsaved' }
    );
  }

  // ── saved on this device; where else? ──
  if (cloud === 'pending') {
    return view(
      'dirty',
      'Saved on this device',
      'Saved on this device. Syncing to your account shortly.',
      SAVE_COMMANDS.openPopover,
      { short: 'Not synced' }
    );
  }
  if (cloud === 'offline') {
    return view(
      'offline',
      'Saved on this device',
      'Saved on this device. Offline — it will sync to your account when you reconnect.',
      SAVE_COMMANDS.openPopover,
      { settled: true }
    );
  }
  if (cloud === 'synced') {
    return view(
      'saved',
      formatSavedAge(savedAt, now),
      'Saved on this device and to your account.',
      SAVE_COMMANDS.openPopover,
      { settled: true }
    );
  }
  return view(
    'saved',
    'Saved on this device',
    'Saved in this browser only. Save to your account to keep it everywhere.',
    SAVE_COMMANDS.openPopover,
    { settled: true }
  );
}

/**
 * "Saved", "Saved 3m ago", … Ported from `ui/toolbar.js` `_formatSavedAge`
 * unchanged, minus the session stopwatch it used to be concatenated with.
 *
 * `now` is injectable so the tests are not a function of when they run — the
 * repo's standing rule that a builder-emitted value must not depend on wall
 * clock applies to what a test asserts, too.
 */
export function formatSavedAge(savedAt, now = Date.now()) {
  if (!savedAt) return 'Saved';
  const seconds = Math.max(0, Math.floor((now - savedAt) / 1000));
  if (seconds < 5) return 'Saved now';
  if (seconds < 60) return `Saved ${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Saved ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `Saved ${hours}h ago`;
}
